import { FormControl, MenuItem, Modal, Select, TextField } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { RxCross1 } from "react-icons/rx";
import { useEffect, useState } from "react";
import { HttpService } from "../../service/HttpService";
import { TextareaAutosize } from "@mui/base/TextareaAutosize";
import FileInput from "../FileInput/FileInput";
import { useAuth } from "../../context/AuthProvider";
import { splitDuration } from "../../Mixin/DisplayDuration";
import SelectInput from "../SelectInput/SelectInput";

const AddEditMilestone = ({
  open,
  handleClose,
  milestone_id,
  isAdd,
  companyId,
  locationId,
  fetchMilestone,
}) => {
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [apiError, setApiError] = useState("");
  const { setShowLoginPopup, showLoginPopup } = useAuth();
  const httpService = new HttpService(setShowLoginPopup);

  const autoSequenceOrder = async (payload) => {
    return await httpService
      .post("milestone/check-auto-sequence", payload)
      .then((response) => {
        if (response?.data?.max_sort_order) {
          formik.setFieldValue("sort_order", response?.data?.max_sort_order);
        } else {
          formik.setFieldValue("sort_order", "");
        }
        return response?.data?.max_sort_order;
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        return error;
      });
  };

  useEffect(() => {
    fetchCompanies();
    if (companyId > 0) {
      getLocationByCompany(companyId);
    }
  }, [companyId, locationId, showLoginPopup]);

  //fetch data for edit time
  useEffect(() => {
    // console.log("useeffect running");
    if (milestone_id !== "") {
      httpService
        .get(`milestone/get_milestone/${milestone_id}`)
        .then((response) => {
          const { days, hours, minutes } =
            splitDuration(
              response?.data[0].days,
              response?.data[0].hours,
              response?.data[0].minutes
            ) || {};

          formik.setValues({
            name: response?.data[0].name,
            durationDays: days || 0,
            durationHours: hours || 0,
            durationMinute: minutes || 0,
            company: response?.data[0].company_id,
            location: response?.data[0].location_id,
            description: response?.data[0].description,
            attached: response?.data[0].attached || [],
            sort_order: response?.data[0].sort_order || "",
            version: response?.data[0].version || "",
          });
          if (response) {
            if (response?.data[0].company_id) {
              getLocationByCompany(response?.data[0].company_id);
            }
          }
        });
    } else {
      formik.resetForm();
    }
  }, [milestone_id]);

  const fetchCompanies = () => {
    httpService
      .get("company/get_companies")
      .then((response) => {
        setCompanies(response?.data?.data);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  };

  const handleChangeLocation = (key, id) => {
    if (key === "company") {
      // Call the API to fetch location data based on the selected company_id
      getLocationByCompany(id);
      formik.setFieldValue("location", "");
      setLocations([]);
    }
  };

  const getLocationByCompany = (id) => {
    return httpService
      .get(`location/get_location/company/${id}`)
      .then((response) => {
        setLocations(response?.data);
        if (response?.data.length === 0) {
          formik.setFieldValue("location", "");
        }
      })
      .catch((error) => {
        console.error("Error fetching location data:", error);
      });
  };

  const handleCloseSave = () => {
    formik.resetForm();
    handleClose();
    setLocations([]);
  };

  const matchSequenceOrder = async (payload) => {
    return await httpService
      .post("milestone/check-sequence", payload)
      .then((response) => {
        setApiError("");
        return response;
      })
      .catch((error) => {
        setApiError(error?.message);
        return error;
      });
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      durationDays: "",
      durationHours: "",
      durationMinute: "",
      company: (companyId > 0 && companyId) || "",
      location: (locationId > 0 && locationId) || "",
      description: "",
      file: [],
      attached: [],
      sort_order: "",
      version: "",
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .trim()
        .required("Please Enter Milestone Name")
        .max(200, "Milestone Name must be less than 200 characters"),
      company: Yup.string().required("Please Select Company"),
      location: Yup.string().required("Please Select Location"),

      durationDays: Yup.number().nullable(),
      durationHours: Yup.number().nullable().max(24, "Maximum 24 hours"),
      durationMinute: Yup.number().nullable().max(60, "Maximum 60 minutes"),
      description: Yup.string().max(
        2000,
        "Description must be less than 2000 characters"
      ),

      file: Yup.mixed()
        // .required("Attachment Is Required")
        .test("fileSize", "Attachment size must be less than 10MB", (value) => {
          if (!value || typeof value !== "object") return true; // Skip if no files provided or value is not an object
          const files = Object.values(value); // Convert object values to an array of files
          return files.every((file) => file.size <= 10485760); // Check size of each file
        })
        .test("fileFormat", "Invalid file format", (value) => {
          if (!value || typeof value !== "object") return true; // Skip if no files provided or value is not an object
          const files = Object.values(value); // Convert object values to an array of files
          if (files.length === 0) return true; // Skip if no files provided
          const allowedExtensions = {
            video: ["mp4", "webm", "mov"],
            pdf: ["pdf"],
            document: ["doc", "docx"],
            spreadsheet: ["xls", "xlsx", "csv"],
            image: ["jpg", "png", "jpeg", "gif", "webp"],
          };
          return files.every((file) => {
            if (!file || !file.name) return false; // If file or file name is undefined, return false
            const fileExtension = file?.name?.split(".").pop().toLowerCase();
            const fileType = Object.keys(allowedExtensions).find((type) =>
              allowedExtensions[type].includes(fileExtension)
            );
            return !!fileType;
          });
        }),
    }).test("at-least-one", null, (values) => {
      const { durationDays, durationHours, durationMinute } = values;
      if (!durationDays && !durationHours && !durationMinute) {
        return new Yup.ValidationError(
          "Please Enter One Duration",
          null,
          "durationDays"
        );
      }
      if (durationDays < 0 || durationHours < 0 || durationMinute < 0) {
        return new Yup.ValidationError(
          "Please Enter Valid Duration",
          null,
          "durationDays"
        );
      }
      return true;
    }),
    onSubmit: async (values) => {
      // console.log(values);
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("days", values.durationDays || 0);
      formData.append("hours", values.durationHours || 0);
      formData.append("minutes", values.durationMinute || 0);
      formData.append("company_id", values.company);
      formData.append("location_id", values.location);
      formData.append("description", values.description || "");
      formData.append("sort_order", values.sort_order);
      formData.append("version", values.version || "");
      // Check if new files are provided
      if (values.file && values.file.length > 0) {
        for (let i = 0; i < values.file.length; i++) {
          formData.append(`attached`, values.file[i]);
        }
      } else {
        formData.append("attached", []);
      }
      //Runs only for edit purpose
      if (values.attached) {
        if (milestone_id !== "" && !isAdd) {
          for (let i = 0; i < values.attached.length; i++) {
            formData.append(`existFile[${i}]`, values.attached[i]);
          }
          // formData.append("existFile", values.attached);
        }
      }
      if (!apiError) {
        try {
          if (isAdd) {
            const response = await httpService.post(
              "milestone/add_milestone",
              formData
            );
            if (response) {
              toast.success("Milestone Added Successfully", {
                position: "top-right",
              });
              formik.resetForm();
              handleClose();
              fetchMilestone();
            }
          } else {
            const res = await httpService.put(
              `${"milestone/edit_milestone"}/${milestone_id}`,
              formData
            );
            if (res) {
              toast.success("Milestone Updated Successfully", {
                position: "top-right",
              });
              // formik.resetForm();
              handleClose();
              fetchMilestone();
            }
          }
        } catch (error) {
          console.log(error);
        }
      }
    },
  });

  useEffect(() => {
    if (
      formik.values?.sort_order === "" &&
      companyId > 0 &&
      locationId > 0 &&
      milestone_id === ""
    ) {
      autoSequenceOrder(
        { company_id: companyId, location_id: locationId },
        formik.setFieldValue
      );
    }
  }, [companyId, locationId, formik.values.sort_order]);

  return (
    <Modal
      className="task-modal"
      open={open}
      onClose={() => {
        handleCloseSave();
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <form onSubmit={formik.handleSubmit} encType="multipart/form-data">
        <div className="modal_block">
          <div className="modal_header">
            <div className="modal_title_wrapper">
              <h2 className="modal_title">
                {isAdd === true ? "Add Milestone" : "Edit Milestone"}
              </h2>
            </div>
            <div className="cancel-btn-wrap">
              <RxCross1
                onClick={() => {
                  handleCloseSave();
                }}
              />
            </div>
          </div>
          <div className="modal_content">
            <div className="row">
              <div className="item_increase">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form_field_wrapper">
                      <label>
                        Name<span style={{ color: "red" }}>*</span>
                      </label>
                      <TextField
                        id="name"
                        InputProps={{}}
                        placeholder="Enter Your Milestone Name"
                        variant="outlined"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.name}
                      />
                      {formik.touched.name && formik.errors.name ? (
                        <div style={{ color: "red" }}>{formik.errors.name}</div>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form_field_wrapper">
                      <label>
                        Duration
                        <span style={{ color: "red" }}>*</span>
                      </label>
                      <TextField
                        type="number"
                        id="durationDays"
                        InputProps={{ inputProps: { min: 0 } }}
                        placeholder="DD"
                        variant="outlined"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.durationDays}
                      />
                      {formik.touched.durationDays &&
                      formik.errors.durationDays ? (
                        <div style={{ color: "red" }}>
                          {formik.errors.durationDays}
                        </div>
                      ) : null}
                      {formik.touched.durationHours &&
                      formik.errors.durationHours ? (
                        <div style={{ color: "red" }}>
                          {formik.errors.durationHours}
                        </div>
                      ) : null}
                      {formik.touched.durationMinute &&
                      formik.errors.durationMinute ? (
                        <div style={{ color: "red" }}>
                          {formik.errors.durationMinute}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form_field_wrapper">
                      <label>&nbsp;</label>
                      <TextField
                        type="number"
                        id="durationHours"
                        InputProps={{ inputProps: { min: 0 } }}
                        placeholder="HH"
                        variant="outlined"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.durationHours}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form_field_wrapper">
                      <label>&nbsp;</label>
                      <TextField
                        type="number"
                        id="durationMinute"
                        InputProps={{ inputProps: { min: 0 } }}
                        placeholder="MM"
                        variant="outlined"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.durationMinute}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <SelectInput
                      label="Company"
                      name="company"
                      value={formik.values.company}
                      options={companies}
                      multiple={false}
                      onChange={(event) => {
                        formik.setFieldValue("company", event.target.value);
                        handleChangeLocation("company", event.target.value);
                      }}
                      onBlur={(e) => formik.handleBlur(e)}
                      error={formik.touched.company && formik.errors.company}
                      required={true}
                      displayEmpty
                      disabled={companyId > 0 ? true : false}
                    />
                  </div>

                  <div className="col-md-6">
                    <SelectInput
                      label="Location"
                      name="location"
                      value={formik.values.location}
                      options={locations}
                      multiple={false}
                      onChange={(event) => {
                        formik.setFieldValue("location", event.target.value);
                        handleChangeLocation("location", event.target.value);
                        const payload = {
                          company_id: formik.values?.company,
                          location_id: event.target.value,
                          sort_order: formik.values?.sort_order,
                          id: milestone_id,
                        };
                        if (
                          formik.values?.company &&
                          formik.values?.sort_order &&
                          event.target.value
                        ) {
                          matchSequenceOrder(payload);
                        } else {
                          setApiError("");
                        }
                        if (milestone_id === "") {
                          autoSequenceOrder(payload);
                        }
                      }}
                      onBlur={(e) => formik.handleBlur(e)}
                      error={formik.touched.location && formik.errors.location}
                      required={true}
                      displayEmpty
                      disabled={locationId > 0 ? true : false}
                    />
                  </div>

                  <div className="col-md-6">
                    <div className="form_field_wrapper">
                      <label>Sequence(#)</label>
                      <TextField
                        name="sort_order"
                        // className={classes.textField}
                        type="number"
                        InputProps={{ inputProps: { min: 0 } }}
                        placeholder="Enter Sequence No."
                        value={formik.values.sort_order}
                        onChange={(event) => {
                          formik.setFieldValue(
                            "sort_order",
                            event.target.value
                          );
                          if (
                            formik.values?.company &&
                            formik.values?.location &&
                            event.target.value
                          ) {
                            const payload = {
                              company_id: formik.values?.company,
                              location_id: formik.values?.location,
                              sort_order: event.target.value,
                              id: milestone_id,
                            };
                            matchSequenceOrder(payload);
                          } else {
                            setApiError("");
                          }
                        }}
                        onBlur={formik.handleBlur}
                      />
                      {apiError && (
                        <span style={{ color: "red" }}>{apiError}</span>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="form_field_wrapper">
                      <label>Reff Doc. Version(s)</label>
                      <TextField
                        name="Reff Doc. Version(s)"
                        type="text"
                        placeholder="Enter Version(s)"
                        value={formik.values.version}
                        onChange={(event) => {
                          formik.setFieldValue("version", event.target.value);
                        }}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="form_field_wrapper">
                      <label>Description</label>
                      <div>
                        <TextareaAutosize
                          id="description"
                          className="w-100 text-area"
                          aria-label="Type Description"
                          minRows={3}
                          placeholder="Type Description"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.description}
                        />
                        {formik.touched.description &&
                        formik.errors.description ? (
                          <div style={{ color: "red" }}>
                            {formik.errors.description}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <FileInput
                    file={formik.values.file}
                    attachment={formik.values.attached}
                    handleBlur={formik.handleBlur}
                    name="file"
                    name2="attached"
                    setFieldValue={(fieldName, value) =>
                      formik.setFieldValue(fieldName, value)
                    }
                  />
                  {formik.errors.file && formik.touched.file && (
                    <span style={{ color: "red" }}>{formik.errors.file}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal_footer">
            <button
              onClick={() => {
                handleCloseSave();
              }}
              className="btn btn-border"
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {isAdd === true ? "Save" : "Update"}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default AddEditMilestone;
